import { AlertDialog, Button, useControllableState } from '@buffer-mono/popcorn'
import type * as React from 'react'
type PlanOverrideSubscription = {
  interval?: string
  periodEnd?: string | null
  isCanceledAtPeriodEnd: boolean
  scheduledUpdate?: {
    effectiveDate?: string | null
  } | null
}

interface PlanOverrideConfirmationDialogProps {
  children?: React.ReactNode
  subscription: PlanOverrideSubscription
  onConfirm: () => Promise<void>
  loading?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    })
  } catch {
    return dateString
  }
}

const getDialogContent = (
  subscription: PlanOverrideSubscription,
): {
  title: string
  description: React.ReactNode
  cancelLabel: string
  confirmLabel: string
} => {
  const scheduledDate = subscription.scheduledUpdate?.effectiveDate
    ? String(subscription.scheduledUpdate.effectiveDate)
    : null
  const periodEndDate =
    subscription.isCanceledAtPeriodEnd && subscription.periodEnd
      ? String(subscription.periodEnd)
      : null
  const changeDate = scheduledDate || periodEndDate

  if (changeDate) {
    return {
      title: 'Heads up, you have a change coming up',
      description: (
        <>
          Your plan is scheduled to change on{' '}
          <strong>{formatDate(changeDate)}</strong>. Making this change now will
          cancel that update.
        </>
      ),
      cancelLabel: 'Keep upcoming change',
      confirmLabel: 'Make this change instead',
    }
  }

  return {
    title: 'Confirm Plan Change',
    description: <>This will update your plan immediately.</>,
    cancelLabel: 'Cancel',
    confirmLabel: 'Continue',
  }
}

export const PlanOverrideConfirmationDialog: React.FC<
  PlanOverrideConfirmationDialogProps
> = ({
  children,
  subscription,
  onConfirm,
  loading = false,
  open: openProp = false,
  onOpenChange,
}) => {
  const [open, setOpen] = useControllableState({
    prop: openProp,
    onChange: onOpenChange,
  })
  const { title, description, cancelLabel, confirmLabel } =
    getDialogContent(subscription)

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      {children && <AlertDialog.Trigger>{children}</AlertDialog.Trigger>}
      <AlertDialog.Content>
        <AlertDialog.Title>{title}</AlertDialog.Title>
        <AlertDialog.Description>{description}</AlertDialog.Description>

        <AlertDialog.Actions>
          <AlertDialog.Cancel>
            <Button variant="secondary" disabled={loading}>
              {cancelLabel}
            </Button>
          </AlertDialog.Cancel>
          <AlertDialog.Action>
            <Button
              variant="primary"
              onClick={onConfirm}
              loading={loading}
              disabled={loading}
            >
              {confirmLabel}
            </Button>
          </AlertDialog.Action>
        </AlertDialog.Actions>
      </AlertDialog.Content>
    </AlertDialog>
  )
}
